import React, { useState } from 'react';
import { AlertTriangle, Check, Lock, Shield, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createPayment, redirectToPayment } from '../../services/paymentService';
type PaymentPlan = 'single' | 'pro';
export function PricingSection() {
  const {
    user
  } = useAuth();
  const [paymentLoading, setPaymentLoading] = useState<PaymentPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handlePayment = async (plan: PaymentPlan) => {
    if (!user?.email || !user.id) {
      setError('Please sign in to continue to secure checkout.');
      return;
    }
    setPaymentLoading(plan);
    setError(null);
    try {
      const response = await createPayment(plan);
      redirectToPayment(response.authorization_url);
    } catch (paymentError: unknown) {
      const message = paymentError instanceof Error ? paymentError.message : 'Payment system is unavailable. Please try again.';
      setError(message);
      setPaymentLoading(null);
    }
  };
  return <section className="relative bg-[#050505] py-24" id="pricing" data-id="element-2791">
      <div className="absolute left-0 top-0 flex h-8 w-full items-center overflow-hidden bg-[#FFD700] shadow-lg" data-id="element-2792">
        <div className="flex whitespace-nowrap animate-marquee" data-id="element-2793">
          {Array.from({
          length: 20
        }, (_, index) => <span key={index} className="mx-4 flex items-center gap-2 text-sm font-black uppercase tracking-widest text-black" data-id="element-2794">
                <AlertTriangle size={16} fill="black" data-id="element-2795" /> CAUTION: TOXICITY
                DETECTED
              </span>)}
        </div>
      </div>
      <style data-id="element-2796">{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .animate-marquee { animation: none; } }
      `}</style>

      <div className="mx-auto max-w-6xl px-6 pt-16" data-id="element-2797">
        <header className="mx-auto mb-16 max-w-2xl text-center" data-id="element-2798">
          <h2 className="text-4xl font-black uppercase leading-none tracking-tight text-white md:text-5xl" data-id="element-2799">
            Invest In Your <span className="text-[#FFD700]" data-id="element-2800">Sanity</span>
          </h2>
          <p className="mt-5 font-mono text-lg leading-relaxed text-gray-400" data-id="element-2801">
            Choose the level of clarity that fits this conversation.
          </p>
        </header>

        {error && <div className="mx-auto mb-10 flex max-w-4xl items-start gap-3 border border-[#FF0000]/30 bg-[#FF0000]/10 p-4" role="alert" data-id="element-2802">
            <AlertTriangle className="mt-0.5 shrink-0 text-[#FF0000]" size={20} data-id="element-2803" />
            <p className="font-mono text-sm text-[#FF0000]" data-id="element-2804">{error}</p>
          </div>}

        <div className="grid items-stretch gap-6 md:grid-cols-3" data-id="element-2805">
          <article className="flex flex-col border border-gray-800 bg-[#0A0A0A] p-7" data-id="element-2806">
            <div className="mb-7 flex items-center justify-between gap-4" data-id="element-2807">
              <h3 className="text-xl font-bold uppercase tracking-wide text-white" data-id="element-2808">
                Free scan
              </h3>
              <span className="border border-gray-700 bg-gray-900 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-300" data-id="element-2809">
                Trial
              </span>
            </div>
            <div className="mb-7 flex items-baseline" data-id="element-2810">
              <span className="text-5xl font-black text-white" data-id="element-2811">$0</span>
              <span className="ml-2 font-mono text-sm uppercase tracking-wide text-gray-500" data-id="element-2812">
                to start
              </span>
            </div>
            <ul className="mb-9 flex-1 space-y-4" data-id="element-2813">
              <PricingFeature text="1 free forensic scan" data-id="element-2814" />
              <PricingFeature text="Basic toxicity detection" data-id="element-2815" />
              <PricingFeature text="Teaser results card" data-id="element-2816" />
              <PricingFeature text="Standard processing speed" data-id="element-2817" />
            </ul>
            <a href="/signup" className="block w-full rounded-lg border border-gray-700 bg-gray-800 py-4 text-center text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2818">
              Start Free Scan
            </a>
          </article>

          <article className="relative flex flex-col border-2 border-[#FFD700] bg-[#0A0A0A] p-7 shadow-[0_0_28px_rgba(255,215,0,0.12)]" data-id="element-2819">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD700] px-4 py-1 text-[10px] font-black uppercase tracking-widest text-black" data-id="element-2820">
              One conversation
            </div>
            <div className="mb-7 mt-2 flex items-center justify-between gap-4" data-id="element-2821">
              <h3 className="text-xl font-black uppercase tracking-wide text-[#FFD700]" data-id="element-2822">
                Single scan
              </h3>
              <Sparkles className="text-[#FFD700]" size={22} data-id="element-2823" />
            </div>
            <div className="mb-7 flex items-baseline" data-id="element-2824">
              <span className="text-5xl font-black text-white" data-id="element-2825">$2.99</span>
              <span className="ml-2 font-mono text-sm uppercase tracking-wide text-gray-400" data-id="element-2826">
                one-time
              </span>
            </div>
            <ul className="mb-9 flex-1 space-y-4" data-id="element-2827">
              <PricingFeature text="1 full forensic report" highlight="gold" data-id="element-2828" />
              <PricingFeature text="Detailed pattern labels" highlight="gold" data-id="element-2829" />
              <PricingFeature text="Specific examples and recommendations" highlight="gold" data-id="element-2830" />
              <PricingFeature text="Shareable result and PDF report" highlight="gold" data-id="element-2831" />
            </ul>
            <button type="button" onClick={() => handlePayment('single')} disabled={paymentLoading !== null} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FFD700] py-4 text-sm font-black uppercase tracking-wider text-black transition-colors hover:bg-[#F5C400] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2832">
              {paymentLoading === 'single' ? <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" data-id="element-2833" />{' '}
                  Processing...
                </> : <>Unlock one scan — $2.99</>}
            </button>
          </article>

          <article className="relative flex flex-col border-2 border-[#FF0000] bg-black p-7 shadow-[0_0_32px_rgba(255,0,0,0.14)]" data-id="element-2834">
            <div className="absolute -top-3 right-5 bg-[#FF0000] px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white" data-id="element-2835">
              65% off
            </div>
            <div className="mb-3 mt-2 flex items-center justify-between gap-4" data-id="element-2836">
              <h3 className="text-xl font-black uppercase tracking-wide text-[#FF0000]" data-id="element-2837">
                Truth seeker
              </h3>
              <Shield className="text-[#FF0000]" size={24} data-id="element-2838" />
            </div>
            <div className="mb-1 flex items-baseline" data-id="element-2839">
              <span className="text-5xl font-black text-white" data-id="element-2840">$9.99</span>
              <span className="ml-2 font-mono text-sm uppercase tracking-wide text-gray-400" data-id="element-2841">
                / month
              </span>
            </div>
            <p className="mb-7 text-xs font-bold uppercase tracking-wide text-[#FFD700]" data-id="element-2842">
              Normally $29.99/mo
            </p>
            <ul className="mb-9 flex-1 space-y-4" data-id="element-2843">
              <PricingFeature text="Unlimited forensic scans" highlight="red" data-id="element-2844" />
              <PricingFeature text="Persona evolution tracking" highlight="red" data-id="element-2845" />
              <PricingFeature text="Monthly clarity reports" highlight="red" data-id="element-2846" />
              <PricingFeature text="Priority processing and PDF case files" highlight="red" data-id="element-2847" />
            </ul>
            <button type="button" onClick={() => handlePayment('pro')} disabled={paymentLoading !== null} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF0000] py-4 text-sm font-black uppercase tracking-wider text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2848">
              {paymentLoading === 'pro' ? <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-2849" />{' '}
                  Processing...
                </> : <>
                  <Shield size={16} fill="white" data-id="element-2850" /> Get full access
                </>}
            </button>
            <span className="mt-5 flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-wide text-gray-500" data-id="element-2851">
              <Lock size={10} data-id="element-2852" /> 30-day money back guarantee
            </span>
          </article>
        </div>
      </div>
    </section>;
}
function PricingFeature({
  text,
  highlight
}: {
  text: string;
  highlight?: 'gold' | 'red';
}) {
  const color = highlight === 'gold' ? 'text-[#FFD700]' : highlight === 'red' ? 'text-[#FF0000]' : 'text-gray-500';
  return <li className="flex items-start gap-3" data-id="element-2853">
      <Check size={16} strokeWidth={3} className={`mt-0.5 shrink-0 ${color}`} data-id="element-2854" />
      <span className={highlight ? 'text-sm font-medium text-white' : 'text-sm font-medium text-gray-400'} data-id="element-2855">
        {text}
      </span>
    </li>;
}